import React, { useRef, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import * as FiIcons from 'react-icons/fi';
import { getBrevoStatus } from '../utils/emailService';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import {
  isSupabaseConfigured,
  syncAllCandidatesToSupabase,
} from '../utils/supabaseClient';

const {
  FiHash, FiLayout, FiMail, FiUploadCloud, FiFileText, FiTrash2, FiEye,
  FiShield, FiDatabase, FiCheck, FiAlertTriangle, FiRefreshCw, FiX,
  FiCheckCircle, FiXCircle, FiLoader, FiChevronDown, FiChevronUp,
  FiInfo, FiUsers,
} = FiIcons;

// ── Confirmation Modal ────────────────────────────────────────────────────────
function ConfirmSyncModal({ total, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 8 }}
        transition={{ duration: 0.18 }}
        className="relative z-10 w-full max-w-md bg-white border border-surface-200 rounded-2xl shadow-2xl p-6 space-y-5"
      >
        {/* Icon */}
        <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
          <FiDatabase className="w-6 h-6 text-amber-500" />
        </div>

        {/* Title */}
        <div>
          <h3 className="text-base font-bold text-navy-900">Sync All Candidates to Supabase?</h3>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            This will write <span className="font-bold text-navy-900">{total} candidate record{total !== 1 ? 's' : ''}</span> to
            Supabase so they are publicly verifiable via QR codes. The operation
            is <span className="font-semibold text-emerald-600">idempotent</span> — existing records are updated,
            never duplicated.
          </p>
        </div>

        {/* Warning note */}
        <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
          <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Only candidates with a <code className="font-mono bg-amber-100 px-1 rounded">Generated ID</code> or{' '}
            <code className="font-mono bg-amber-100 px-1 rounded">Verification ID</code> will be synced.
            Candidates without either will be skipped.
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-surface-200 text-sm font-semibold text-slate-600 hover:bg-surface-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold transition-colors shadow-sm"
          >
            Confirm & Sync
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────
export default function Settings() {
  const { mode, settings, updateSettings, uploadCustomTemplate, allData, candidates, emailHistory, updateCandidate } = usePlatform();
  const [checkingBrevo, setCheckingBrevo] = React.useState(false);
  const fileInputRef = useRef(null);

  // ── Supabase Sync state ───────────────────────────────────────────────────
  const [showConfirm, setShowConfirm]   = useState(false);
  const [syncing, setSyncing]           = useState(false);
  const [progress, setProgress]         = useState(null);   // { done, total }
  const [syncResult, setSyncResult]     = useState(null);   // final result object
  const [showErrors, setShowErrors]     = useState(false);

  // Gather ALL candidates across both modes (internship + certificate)
  const allCandidates = [
    ...(allData?.internship?.candidates  || []),
    ...(allData?.certificate?.candidates || []),
  ];

  const handleSyncStart = useCallback(async () => {
    setShowConfirm(false);
    setSyncing(true);
    setProgress({ done: 0, total: allCandidates.length });
    setSyncResult(null);
    setShowErrors(false);

    const result = await syncAllCandidatesToSupabase(
      allCandidates,
      ({ done, total }) => setProgress({ done, total }),
    );

    setSyncing(false);
    setSyncResult(result);

    // Toast feedback — only claim full success when there are no failures
    if (result.failed === 0) {
      toast.success(
        `Sync complete — ${result.synced} synced, ${result.skipped} skipped.`,
      );
    } else {
      toast.error(
        `Sync finished with ${result.failed} failure${result.failed !== 1 ? 's' : ''}. Check the error list below.`,
      );
    }
  }, [allCandidates]);

  const progressPct = progress
    ? Math.round((progress.done / Math.max(progress.total, 1)) * 100)
    : 0;

  // ── Email Status Reconciliation ──────────────────────────────────────────
  // Reads emailHistory (written during bulk send — contains Brevo messageId
  // as record.id). Matches candidates by email address (primary) or
  // candidateId (secondary) and updates emailStatus without resending.
  const [reconPreview, setReconPreview] = useState(null);  // { toUpdate[], byId, byEmail, histSent, histFailed, alreadySent }
  const [reconRunning, setReconRunning] = useState(false);
  const [reconResult, setReconResult]   = useState(null);  // { updated, skipped }

  // Determine whether a history record was confirmed by Brevo
  // (real messageId looks like "<...@smtp-relay.brevo.com>" or similar long string,
  // vs the fallback `crypto.randomUUID()` which is a plain 36-char hex UUID).
  const isBrevoConfirmedId = (id) => {
    if (!id) return false;
    // Brevo messageIds start with '<' or are 40+ char SMTP identifiers
    if (id.startsWith('<') && id.endsWith('>')) return true;
    // Some Brevo IDs are long alphanumeric without dashes (>32 chars)
    const noDash = id.replace(/-/g, '');
    return noDash.length > 32 && !id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
  };

  const buildReconPreview = useCallback(() => {
    // All email history for current mode
    const modeHistory = allData?.[mode]?.emailHistory || [];
    const allModeCandidates = allData?.[mode]?.candidates || [];

    // Sent records that have a real Brevo messageId
    const histSentConfirmed = modeHistory.filter(
      h => h.status === 'sent' && isBrevoConfirmedId(h.id)
    );
    const histSentUnconfirmed = modeHistory.filter(
      h => h.status === 'sent' && !isBrevoConfirmedId(h.id)
    );
    const histFailed = modeHistory.filter(h => h.status === 'failed');

    // Build lookup maps from history
    // Primary match: by candidateId
    const histById = new Map(); // candidateId → best history record
    // Secondary match: by email address
    const histByEmail = new Map(); // email → best history record

    for (const h of histSentConfirmed) {
      if (h.candidateId && !histById.has(h.candidateId)) histById.set(h.candidateId, h);
      if (h.to && !histByEmail.has(h.to.toLowerCase())) histByEmail.set(h.to.toLowerCase(), h);
    }

    // Partition candidates
    const alreadySent = [];
    const toUpdate = [];  // will be updated
    const noMatch = [];   // no history record found

    for (const c of allModeCandidates) {
      if (c.emailStatus === 'sent') { alreadySent.push(c); continue; }

      // Try ID match first, then email fallback
      const byId    = histById.get(c.id);
      const byEmail = histByEmail.get(c.email?.toLowerCase());
      const match   = byId || byEmail;

      if (match) {
        toUpdate.push({
          candidate: c,
          histRecord: match,
          matchType: byId ? 'id' : 'email',
        });
      } else {
        noMatch.push(c);
      }
    }

    setReconPreview({
      toUpdate,
      noMatch,
      alreadySent,
      histSentConfirmed: histSentConfirmed.length,
      histSentUnconfirmed: histSentUnconfirmed.length,
      histFailed: histFailed.length,
      totalHistory: modeHistory.length,
    });
  }, [allData, mode]);

  const applyReconciliation = useCallback(async () => {
    if (!reconPreview || reconPreview.toUpdate.length === 0) return;
    setReconRunning(true);

    let updated = 0;
    for (const { candidate, histRecord } of reconPreview.toUpdate) {
      updateCandidate(candidate.id, {
        emailStatus: 'sent',
        lastEmailSentAt: histRecord.sentAt,
        brevoMessageId: histRecord.id,  // the actual Brevo messageId
        lastError: null,
      });
      updated++;
      // Tiny yield to keep UI responsive during large batches
      if (updated % 50 === 0) await new Promise(r => setTimeout(r, 10));
    }

    setReconRunning(false);
    setReconResult({ updated, skipped: reconPreview.noMatch.length });
    setReconPreview(null);
    toast.success(`✅ Reconciled ${updated} candidate${updated !== 1 ? 's' : ''} to emailStatus = sent.`);
  }, [reconPreview, updateCandidate]);

  // ── Shared card wrapper (same as before) ─────────────────────────────────
  const SectionCard = ({ icon: Icon, iconColor, title, subtitle, badge, children }) => (
    <section className="glass-card rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between flex-wrap gap-4 px-7 py-5 border-b border-surface-100">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${iconColor} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-navy-900">{title}</h2>
            <p className="text-xs text-slate-800 font-semibold mt-0.5">{subtitle}</p>
          </div>
        </div>
        {badge && (
          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold border ${badge.color}`}>
            {badge.label}
          </span>
        )}
      </div>
      <div className="p-7">{children}</div>
    </section>
  );

  const handleTemplateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf' || file.type.includes('image')) {
        uploadCustomTemplate(file);
        toast.success(`Template "${file.name}" uploaded successfully!`);
      } else {
        toast.error("Please upload a PDF or Image template.");
      }
    }
  };

  return (
    <div className="max-w-5xl space-y-8 pb-20">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="page-title capitalize">{mode} Configuration</h1>
        <p className="page-subtitle">Manage global rules, automation, and document templates.</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ID Config */}
        <SectionCard icon={FiHash} iconColor="bg-brand-50 text-brand-600" title="Smart ID Format" subtitle="Automated sequence generation">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Prefix</label>
                <input type="text" value={settings.idPrefix}
                  onChange={(e) => updateSettings({ idPrefix: e.target.value })}
                  className="input-field font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Sequence Year</label>
                <input type="text" value={settings.idYear}
                  onChange={(e) => updateSettings({ idYear: e.target.value })}
                  className="input-field font-mono" />
              </div>
            </div>
            <div className="p-4 bg-surface-50 rounded-xl border border-surface-200 border-dashed">
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Generated ID Preview</div>
              <div className="text-xl font-bold text-brand-600 font-mono tracking-tight">
                {settings.idPrefix}-{settings.idYear}-{'0'.repeat(settings.idLength - 1)}1
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Template Upload */}
        <SectionCard icon={FiUploadCloud} iconColor="bg-violet-50 text-violet-600" title="Template Upload" subtitle="Upload custom designs (PDF/SVG/JPG)">
          <div className="space-y-5">
            <input type="file" className="hidden" ref={fileInputRef} onChange={handleTemplateUpload} accept=".pdf,image/*" />
            <div
              onClick={() => fileInputRef.current.click()}
              className="border-2 border-dashed border-surface-200 rounded-xl p-8 flex flex-col items-center justify-center hover:border-brand-300 hover:bg-brand-50/30 cursor-pointer transition-all group"
            >
              <FiFileText className="w-8 h-8 text-slate-300 mb-3 group-hover:text-brand-500 transition-colors" />
              <span className="text-sm font-medium text-slate-500 group-hover:text-slate-700">Click to upload template</span>
              <span className="text-[10px] text-slate-700 font-bold mt-1">PDF or Images only</span>
            </div>

            {settings.customTemplates.length > 0 && (
              <div className="space-y-2">
                <div className="section-title px-1">Uploaded Templates</div>
                {settings.customTemplates.map((tpl) => (
                  <div key={tpl.id} className="flex items-center justify-between p-3 bg-surface-50 rounded-xl border border-surface-200 group">
                    <div className="flex items-center gap-3">
                      <FiFileText className="text-violet-500 w-4 h-4" />
                      <span className="text-sm font-medium text-navy-900 truncate max-w-[150px]">{tpl.name}</span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-1.5 text-slate-400 hover:text-brand-600 rounded-lg"><FiEye className="w-4 h-4" /></button>
                      <button className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg"><FiTrash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Lifecycle */}
      <SectionCard icon={FiShield} iconColor="bg-emerald-50 text-emerald-600" title="Lifecycle & Credential Settings" subtitle="Control validity and distribution">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Credential Validity</label>
            <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl">
              <FiShield className="w-5 h-5 flex-shrink-0 text-emerald-600" />
              <div>
                <div className="text-xs font-semibold text-emerald-700">Lifetime Validity (Inbuilt)</div>
                <div className="text-[11px] text-emerald-600/80 mt-0.5">Permanent · Never expires</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Automated Email Triggers</label>
            <div className="flex flex-wrap gap-2">
              {['Upon Generation', 'On Selection', 'On Completion', 'Weekly Reminders'].map(trigger => (
                <button key={trigger} className="btn-secondary text-xs py-2 px-3.5">
                  {trigger}
                </button>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Brevo API */}
      <SectionCard icon={FiMail} iconColor="bg-sky-50 text-sky-600" title="Brevo Transactional Email API"
        subtitle="Server-side secure transactional dispatch"
        badge={{ label: 'REST API v3', color: 'bg-sky-50 text-sky-600 border-sky-100' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 bg-surface-50 rounded-xl border border-surface-200 space-y-3 text-sm">
            {[
              { label: 'Sender Name', value: 'Innovation Hacks', style: 'text-navy-900 font-medium' },
              { label: 'Sender Email', value: 'innovationhacksofficial@gmail.com', style: 'font-mono text-brand-600 text-xs' },
              { label: 'API Key Storage', value: 'Secure Server Environment (.env)', style: 'text-emerald-600 font-medium', dot: true },
            ].map((item, i) => (
              <div key={i} className={`flex justify-between items-center ${i < 2 ? 'pb-3 border-b border-surface-100' : ''}`}>
                <span className="section-title">{item.label}</span>
                <span className={item.style}>
                  {item.dot && <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5 align-middle" />}
                  {item.value}
                </span>
              </div>
            ))}
          </div>
          <div className="p-5 bg-surface-50 rounded-xl border border-surface-200 flex flex-col justify-between">
            <div>
              <div className="text-sm font-semibold text-navy-900 mb-1">Configuration Notice</div>
              <p className="text-xs text-slate-500 leading-relaxed">
                To update the Brevo API key or change the verified sender address, modify <code className="text-brand-600 font-mono text-[11px]">BREVO_API_KEY</code> and <code className="text-brand-600 font-mono text-[11px]">BREVO_SENDER_EMAIL</code> in your server's <code className="text-brand-600 font-mono text-[11px]">.env</code> file.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-surface-100 flex items-center justify-between">
              <span className="text-[10px] text-slate-700 font-bold font-mono">No client-side key exposure</span>
              <button
                disabled={checkingBrevo}
                onClick={async () => {
                  setCheckingBrevo(true);
                  const toastId = toast.loading('Connecting to Brevo API...');
                  try {
                    const status = await getBrevoStatus();
                    if (status.connected || status.configured) {
                      toast.success(`✅ Brevo Connected! Sender: ${status.senderName || 'Innovation Hacks'}`, { id: toastId });
                    } else {
                      toast.error('❌ Brevo key not found in server .env', { id: toastId });
                    }
                  } catch (e) {
                    toast.error(`❌ Connection failed: ${e.message}`, { id: toastId });
                  } finally {
                    setCheckingBrevo(false);
                  }
                }}
                className="btn-primary text-xs py-2 disabled:opacity-50">
                {checkingBrevo ? 'Verifying...' : 'Verify Connection'}
              </button>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Supabase Credential Sync ──────────────────────────────────────────── */}
      <SectionCard
        icon={FiDatabase}
        iconColor="bg-indigo-50 text-indigo-600"
        title="Supabase Credential Sync"
        subtitle="Push localStorage candidates to Supabase for public QR verification"
        badge={
          isSupabaseConfigured
            ? { label: 'Connected', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' }
            : { label: 'Not Configured', color: 'bg-rose-50 text-rose-500 border-rose-100' }
        }
      >
        <div className="space-y-6">

          {/* Info row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: 'Total Candidates',
                value: allCandidates.length,
                sub: 'across all programs',
                color: 'bg-slate-50 border-slate-200 text-slate-700',
              },
              {
                label: 'Syncable',
                value: allCandidates.filter(c => c.generatedId || c.verificationId).length,
                sub: 'have a verifiable ID',
                color: 'bg-indigo-50 border-indigo-100 text-indigo-700',
              },
              {
                label: 'Will Be Skipped',
                value: allCandidates.filter(c => !c.generatedId && !c.verificationId).length,
                sub: 'no ID to sync',
                color: 'bg-amber-50 border-amber-100 text-amber-700',
              },
            ].map(({ label, value, sub, color }) => (
              <div key={label} className={`p-4 rounded-xl border ${color} text-center`}>
                <div className="text-2xl font-black">{value}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider mt-0.5">{label}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          <p className="text-xs text-slate-500 leading-relaxed">
            Candidates stored only in <span className="font-semibold text-slate-700">localStorage</span> cannot
            be found by the public QR verification page, which reads from Supabase. Run this sync to make all
            existing credentials publicly verifiable. The operation is <span className="font-semibold text-emerald-600">idempotent</span> —
            existing Supabase records are updated, never duplicated. Re-running is safe.
          </p>

          {/* Progress bar — shown while syncing */}
          {syncing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                <span className="flex items-center gap-1.5">
                  <FiLoader className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                  Syncing candidates…
                </span>
                <span className="font-mono">{progress?.done ?? 0} / {progress?.total ?? 0}</span>
              </div>
              <div className="w-full h-2.5 bg-surface-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-indigo-500 to-brand-500 rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ ease: 'linear', duration: 0.15 }}
                />
              </div>
              <div className="text-[10px] text-slate-400 font-mono text-right">{progressPct}%</div>
            </div>
          )}

          {/* Result panel — shown after sync completes */}
          {syncResult && !syncing && (
            <div className={`rounded-xl border p-5 space-y-4 ${
              syncResult.failed > 0 ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'
            }`}>
              {/* Summary header */}
              <div className="flex items-center gap-2">
                {syncResult.failed > 0 ? (
                  <FiAlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0" />
                ) : (
                  <FiCheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                )}
                <span className={`text-sm font-bold ${syncResult.failed > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                  {syncResult.failed > 0
                    ? `Sync completed with ${syncResult.failed} failure${syncResult.failed !== 1 ? 's' : ''}`
                    : 'All eligible candidates synced successfully'}
                </span>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total', value: syncResult.total,   color: 'text-slate-700',   bg: 'bg-white/70' },
                  { label: 'Synced',  value: syncResult.synced,  color: 'text-emerald-700', bg: 'bg-emerald-100/60' },
                  { label: 'Skipped', value: syncResult.skipped, color: 'text-amber-700',   bg: 'bg-amber-100/60' },
                  { label: 'Failed',  value: syncResult.failed,  color: 'text-rose-700',    bg: 'bg-rose-100/60' },
                ].map(({ label, value, color, bg }) => (
                  <div key={label} className={`${bg} rounded-xl p-3 text-center border border-white/50`}>
                    <div className={`text-xl font-black ${color}`}>{value}</div>
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${color} opacity-80`}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Error list — collapsible */}
              {syncResult.errors.length > 0 && (
                <div className="space-y-2">
                  <button
                    onClick={() => setShowErrors(v => !v)}
                    className="flex items-center gap-1.5 text-xs font-bold text-rose-600 hover:text-rose-700 transition-colors"
                  >
                    {showErrors ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
                    {showErrors ? 'Hide' : 'Show'} failed records ({syncResult.errors.length})
                  </button>
                  {showErrors && (
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                      {syncResult.errors.map(({ id, error }, i) => (
                        <div key={i} className="flex items-start gap-2 p-2.5 bg-white/80 rounded-lg border border-rose-200 text-xs">
                          <FiXCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <span className="font-mono font-bold text-slate-700 break-all">{id}</span>
                            <p className="text-rose-600 mt-0.5 break-words">{error}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Not configured notice */}
          {!isSupabaseConfigured && (
            <div className="flex items-start gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
              <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Supabase is not configured. Add <code className="font-mono bg-rose-100 px-1 rounded">VITE_SUPABASE_URL</code> and{' '}
                <code className="font-mono bg-rose-100 px-1 rounded">VITE_SUPABASE_ANON_KEY</code> to your <code className="font-mono bg-rose-100 px-1 rounded">.env</code> file.
                The anon key is safe to expose in frontend code — it is governed by Row Level Security.
              </span>
            </div>
          )}

          {/* Action button */}
          <div className="flex items-center gap-3 pt-1">
            <button
              id="sync-all-candidates-btn"
              disabled={!isSupabaseConfigured || syncing}
              onClick={() => setShowConfirm(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
            >
              {syncing ? (
                <FiLoader className="w-4 h-4 animate-spin" />
              ) : (
                <FiRefreshCw className="w-4 h-4" />
              )}
              {syncing ? `Syncing… (${progress?.done ?? 0}/${progress?.total ?? 0})` : 'Sync All Candidates'}
            </button>
            {syncResult && !syncing && (
              <button
                onClick={() => { setSyncResult(null); setShowErrors(false); }}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-700 rounded-xl border border-surface-200 hover:bg-surface-50 transition-colors"
              >
                <FiX className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>

          <p className="text-[10px] text-slate-400 font-mono">
            Only the Supabase anon key (public, RLS-governed) is used. No service-role credentials are exposed.
          </p>
        </div>
      </SectionCard>

      {/* ── Email Status Reconciliation ─────────────────────────────────── */}
      <SectionCard
        icon={FiUsers}
        iconColor="bg-violet-50 text-violet-600"
        title="Email Status Reconciliation"
        subtitle="Restore 'sent' status from Brevo-confirmed email history — no emails are resent"
        badge={{ label: 'Admin Tool', color: 'bg-violet-50 text-violet-700 border-violet-200' }}
      >
        <div className="space-y-4">
          {/* Explanation */}
          <div className="flex items-start gap-2.5 p-4 bg-violet-50 border border-violet-100 rounded-xl text-xs text-violet-800">
            <FiInfo className="w-4 h-4 flex-shrink-0 mt-0.5 text-violet-500" />
            <div className="space-y-1 leading-relaxed">
              <p>
                <strong>Why use this?</strong> If candidates were re-imported after a bulk send, their
                new records have <code className="font-mono bg-violet-100 px-1 rounded">emailStatus: 'pending'</code> even though
                the emails were accepted by Brevo. This tool reads <strong>emailHistory</strong> (which
                was written during the original send and contains real Brevo messageIds) and safely
                reconciles candidates by matching on <strong>email address</strong>.
              </p>
              <p className="text-violet-600">
                Only records backed by a confirmed Brevo messageId are applied.
                This tool <em>never</em> resends emails and <em>never</em> fabricates status.
              </p>
            </div>
          </div>

          {/* Diagnostic summary from reconPreview */}
          {reconPreview && (
            <div className="space-y-3">
              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'History records total', value: reconPreview.totalHistory, color: 'bg-slate-50 border-slate-200 text-slate-700' },
                  { label: 'Brevo-confirmed sent', value: reconPreview.histSentConfirmed, color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                  { label: 'Already marked sent', value: reconPreview.alreadySent.length, color: 'bg-sky-50 border-sky-200 text-sky-700' },
                  { label: 'Failed history records', value: reconPreview.histFailed, color: 'bg-rose-50 border-rose-200 text-rose-700' },
                ].map(s => (
                  <div key={s.label} className={`p-3 rounded-xl border ${s.color} text-center`}>
                    <div className="text-2xl font-black">{s.value}</div>
                    <div className="text-[10px] font-semibold mt-0.5 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Reconciliation summary */}
              <div className={`p-4 rounded-xl border text-xs font-medium space-y-1 ${
                reconPreview.toUpdate.length > 0
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-700'
              }`}>
                <div className="font-bold text-sm">
                  {reconPreview.toUpdate.length > 0
                    ? `${reconPreview.toUpdate.length} candidate${reconPreview.toUpdate.length !== 1 ? 's' : ''} will be updated to emailStatus = sent`
                    : '✅ All eligible candidates are already marked as sent'}
                </div>
                {reconPreview.toUpdate.length > 0 && (
                  <div className="text-amber-600">
                    Matched by candidateId: {reconPreview.toUpdate.filter(x => x.matchType === 'id').length} ·
                    Matched by email address: {reconPreview.toUpdate.filter(x => x.matchType === 'email').length}
                  </div>
                )}
                {reconPreview.noMatch.length > 0 && (
                  <div className="text-slate-500">
                    {reconPreview.noMatch.length} candidate{reconPreview.noMatch.length !== 1 ? 's' : ''} have no matching history record and will remain unchanged.
                  </div>
                )}
                {reconPreview.histSentUnconfirmed > 0 && (
                  <div className="text-slate-500">
                    {reconPreview.histSentUnconfirmed} history records with unconfirmed IDs (fallback UUIDs) were excluded for safety.
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3">
                {reconPreview.toUpdate.length > 0 && (
                  <button
                    onClick={applyReconciliation}
                    disabled={reconRunning}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
                  >
                    {reconRunning
                      ? <><FiLoader className="w-4 h-4 animate-spin" /> Applying…</>
                      : <><FiCheck className="w-4 h-4" /> Apply Reconciliation ({reconPreview.toUpdate.length})</>}
                  </button>
                )}
                <button
                  onClick={() => setReconPreview(null)}
                  className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-700 rounded-xl border border-surface-200 hover:bg-surface-50 transition-colors"
                >
                  <FiX className="w-3.5 h-3.5 inline mr-1.5" />Cancel
                </button>
              </div>
            </div>
          )}

          {/* Result after applying */}
          {reconResult && !reconPreview && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800 flex items-center gap-3">
              <FiCheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <span className="font-bold">{reconResult.updated} candidate{reconResult.updated !== 1 ? 's' : ''}</span> successfully reconciled to{' '}
                <code className="font-mono bg-emerald-100 px-1 rounded">emailStatus = sent</code>.
                {reconResult.skipped > 0 && (
                  <span className="text-emerald-600"> · {reconResult.skipped} had no matching history and were left unchanged.</span>
                )}
                <button
                  onClick={() => setReconResult(null)}
                  className="ml-3 text-xs underline text-emerald-600 hover:text-emerald-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Initial action button */}
          {!reconPreview && !reconResult && (
            <button
              onClick={buildReconPreview}
              className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
            >
              <FiDatabase className="w-4 h-4" />
              Analyse Email History
            </button>
          )}
        </div>
      </SectionCard>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirm && (
          <ConfirmSyncModal
            total={allCandidates.filter(c => c.generatedId || c.verificationId).length}
            onConfirm={handleSyncStart}
            onCancel={() => setShowConfirm(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}